/**
 * Adaptador de la plantilla de carga masiva del SISTEMA ANTERIOR.
 *
 * El sistema viejo genera un archivo con hoja `IntegrationCCWV2` (o `FormatoMasivo`) y
 * 23 columnas fijas — "Numero de Guía / Tracking aliado", "Nº Pieza", "Categoria de la
 * pieza"… — que NO coinciden con las cabeceras de la plantilla vigente
 * (`exportFormatoShipmentXLSX_V2WithCoords`). Antes de esto, subir uno de esos archivos
 * moría en "Formato incorrecto — headers faltantes".
 *
 * En vez de duplicar el parser, acá SOLO se traducen las CABECERAS: se devuelve un
 * `headerMap` (nombre de cabecera vigente → letra de columna del archivo viejo) que el
 * flujo actual consume sin enterarse. Las FILAS no se tocan: los valores del formato
 * viejo ya son compatibles con `normalizeShipmentByVersion` —
 *   - "DOMICILIO"/"OFICINA"   → ya los resuelve el normalizador,
 *   - ciudad "CCS"            → matchea por locationCode,
 *   - tienda "8204"/"AAL"     → matchea por buCode,
 *   - documento "V"           → matchea por abbreviationName,
 *   - categoría "49"          → `deriveCategoryId` la resuelve por id.
 *
 * Equivalencia clave: "Nº Pieza" del formato viejo es la CANTIDAD de piezas iguales de
 * esa fila, no un índice — el flujo legacy hacía `for (i = 0; i < parseInt(Q); i++)`
 * (ImportsMasiveTable). Por eso mapea a "Cantidad de Piezas".
 *
 * Lo que el formato viejo NO trae (coordenadas, destinatario favorito, oficina origen,
 * servicio, punto de referencia) queda apuntando a una columna inexistente, así que el
 * parser lee '' y aplica sus defaults de siempre. `unmapped` los enumera para poder
 * avisarle al operador qué tiene que revisar.
 */

// Columna que no existe en el archivo viejo. El parser hace `String(row[letra] ?? '')`,
// así que cualquier cabecera apuntada acá se lee como vacía (igual que una celda en
// blanco de la plantilla vigente) en vez de romper el flujo.
export const LEGACY_ABSENT_COLUMN = '__legacy_absent_column__'

// Marcas diacríticas combinantes (U+0300–U+036F). Se construye desde un string escapado
// y no desde un literal de regex: escrito como clase de caracteres, el rango queda en el
// fuente como dos marcas combinantes INVISIBLES, que cualquier editor o normalización de
// texto puede corromper sin que se note en el diff.
const COMBINING_MARKS = new RegExp(
  `[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`,
  'g'
)

/**
 * Clave canónica de una cabecera: sin tildes, en minúsculas y con todo lo que no sea
 * alfanumérico colapsado a un espacio. Así "Nº de Documento destinatario",
 * "N° de documento destinatario" y "Nº  de  DOCUMENTO  Destinatario" caen en la misma
 * clave, que es justo la clase de diferencia que trae un archivo de otro sistema.
 *
 * NFD separa la tilde de su letra y el rango ̀-ͯ (marcas diacríticas
 * combinantes) la borra: "á" → "a" + tilde → "a".
 */
export const normalizeHeaderKey = (raw: unknown): string =>
  String(raw ?? '')
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

/**
 * Cabecera de la plantilla VIGENTE → cabeceras equivalentes del formato anterior.
 * Se comparan normalizadas (ver normalizeHeaderKey), por eso conviven variantes con y
 * sin tilde y la errata heredada "Dimension Anchode la pieza" (exports.tsx:624).
 *
 * INVARIANTE: un mismo alias normalizado NO puede aparecer bajo dos cabeceras canónicas
 * distintas — si pasa, el mapeo queda decidido por el orden de las claves y una columna
 * se leería de la otra en silencio. Hay un test que lo verifica.
 */
export const LEGACY_ALIASES: Record<string, string[]> = {
  'Número de guía aliada': [
    'Numero de Guía / Tracking aliado',
    'Numero de Guia / Tracking aliado',
    'Número de guía / Tracking aliado',
    'Numero de guia aliado',
    'Tracking aliado',
  ],
  'Tipo de entrega': [
    'Tipo de Entrega',
    'Tipo de retiro',
    'Tipo de entrega (retiro por oficina/domicilio)',
  ],
  'Ciudad/Localidad destino (si es domicilio)': [
    'Ciudad Destino',
    'Ciudad destino',
  ],
  'Código de tienda destino (si es entrega en tienda)': [
    'Codigo Tienda destinatario',
    'Código Tienda destinatario',
    'Código de tienda destino (si es retiro por oficina)',
  ],
  'Tipo de documento Remitente (Opcional)': [
    'Tipo de Documento Remitente',
    'Tipo de documento Remitente',
  ],
  'Nº de Documento Remitente (Opcional)': [
    'Nº de Documento Remitente',
    'N° de Documento Remitente',
  ],
  'Nombre Remitente (Opcional)': ['Nombre Remitente'],
  'Tipo de documento destinatario': ['Tipo de Documento destinatario'],
  'Nº de Documento destinatario': ['N° de Documento destinatario'],
  'Nombre destinatario': ['Nombre Destinatario'],
  'Telefono destinatario': ['Teléfono destinatario', 'Telefono Destinatario'],
  'Correo destinatario': ['Correo Destinatario'],
  'Dirección destino (si es domicilio)': [
    'Dirección Destino',
    'Direccion Destino',
    'Dirección destino',
  ],
  'Codigo Postal (Opcional)': ['Codigo Postal', 'Código Postal'],
  Observaciones: ['Observaciones'],
  // "Nº Pieza" = cantidad de piezas iguales de la fila (ver cabecera del archivo).
  'Cantidad de Piezas': [
    'Nº Pieza',
    'N° Pieza',
    'Nro Pieza',
    'Cantidad de piezas',
  ],
  'Peso en Kg por pieza': ['Peso en Kg de la pieza'],
  'Dimension Alto por pieza': [
    'Dimension Alto de la pieza',
    'Dimensión Alto de la pieza',
  ],
  'Dimension Ancho por pieza': [
    'Dimension Ancho de la pieza',
    'Dimension Anchode la pieza',
    'Dimensión Ancho de la pieza',
  ],
  'Dimension Largo por pieza': [
    'Dimension Largo de la pieza',
    'Dimensión Largo de la pieza',
  ],
  'Valor declarado por pieza': ['Valor declarado de la pieza'],
  // El formato viejo trae el ID de categoría (ej "49"); `deriveCategoryId` lo resuelve
  // tanto por `code` como por `id`, así que entra por la columna de código.
  'Código de categoría': [
    'Categoria de la pieza',
    'Categoría de la pieza',
    'Codigo producto',
    'Código producto',
  ],
  'Categoria del contenido': ['Nombre producto'],
}

// Índice inverso ya normalizado: cabecera canónica → sus claves aceptadas.
const ALIAS_INDEX: Record<string, string[]> = Object.entries(
  LEGACY_ALIASES
).reduce<Record<string, string[]>>((acc, [canonical, aliases]) => {
  acc[normalizeHeaderKey(canonical)] = [
    normalizeHeaderKey(canonical),
    ...aliases.map(normalizeHeaderKey),
  ]
  return acc
}, {})

// Claves aceptadas para una cabecera canónica. El fallback NO es decorativo: sin él, una
// cabecera fuera de LEGACY_ALIASES devuelve undefined y el spread/filter de más abajo
// tira TypeError en tiempo de CARGA del módulo — es decir, pantalla en blanco en toda la
// sección, no un error de importación recuperable.
const aliasKeysFor = (canonical: string): string[] => {
  const key = normalizeHeaderKey(canonical)
  return ALIAS_INDEX[key] ?? [key]
}

// Cabecera exclusiva de la plantilla vigente. Si está, el archivo NO es del sistema
// anterior y no hay nada que adaptar (evita "adaptar" un archivo actual mal llenado).
const CURRENT_TEMPLATE_SIGNATURE = normalizeHeaderKey('Número de guía aliada')

// Marcadores del formato anterior. Se exige el tracking + al menos una columna de
// pieza: con eso alcanza para distinguirlo de cualquier otro Excel suelto.
const LEGACY_TRACKING_KEYS = aliasKeysFor('Número de guía aliada').filter(
  (k) => k !== CURRENT_TEMPLATE_SIGNATURE
)
const LEGACY_PIECE_KEYS = [
  ...aliasKeysFor('Peso en Kg por pieza'),
  ...aliasKeysFor('Cantidad de Piezas'),
  ...aliasKeysFor('Código de categoría'),
]

// Sin estas columnas el archivo viejo no alcanza para armar una guía: se rechaza con un
// mensaje concreto en vez de importar filas mutiladas.
const ESSENTIAL_HEADERS = [
  'Número de guía aliada',
  'Tipo de entrega',
  'Nombre destinatario',
  'Peso en Kg por pieza',
  'Dimension Alto por pieza',
  'Dimension Ancho por pieza',
  'Dimension Largo por pieza',
]

export interface LegacyTemplateAdaptation {
  /** headerMap listo para el parser vigente: cabecera actual (lowercase) → letra. */
  headerMap: Record<string, string>
  /** Cabeceras vigentes que sí existen en el archivo viejo. */
  mapped: string[]
  /** Cabeceras vigentes sin equivalente (se leerán vacías). */
  unmapped: string[]
  /** Cabeceras imprescindibles que faltan → no se puede adaptar. */
  missingEssential: string[]
}

/** Re-indexa el headerMap del parser (cabecera lowercase → letra) por clave normalizada. */
const byNormalizedKey = (
  headerMap: Record<string, string>
): Record<string, string> => {
  const out: Record<string, string> = {}
  Object.entries(headerMap).forEach(([header, letter]) => {
    const key = normalizeHeaderKey(header)
    // Primera ocurrencia gana: si el archivo repite una cabecera, la columna válida es
    // la de más a la izquierda (la que el operador ve como "la" columna).
    if (key && !(key in out)) out[key] = letter
  })
  return out
}

/**
 * ¿El archivo subido viene del sistema anterior?
 * @param headerMap cabecera (lowercase, tal cual lo arma el parser) → letra de columna.
 */
export const isLegacyBulkTemplate = (
  headerMap: Record<string, string>
): boolean => {
  const keys = byNormalizedKey(headerMap)
  if (keys[CURRENT_TEMPLATE_SIGNATURE]) return false
  const hasTracking = LEGACY_TRACKING_KEYS.some((k) => keys[k])
  const hasPieceColumn = LEGACY_PIECE_KEYS.some((k) => keys[k])
  return hasTracking && hasPieceColumn
}

/**
 * Traduce las cabeceras del formato anterior a las de la plantilla vigente.
 *
 * @param headerMap cabecera (lowercase) → letra, tal cual lo arma el parser.
 * @param canonicalHeaders cabeceras que el parser vigente necesita resolver. Se recibe
 *   por parámetro (y no se copia acá) para que no pueda desincronizarse de la lista real
 *   que valida `ShipmentBulkUpload`.
 */
export const adaptLegacyBulkTemplate = (
  headerMap: Record<string, string>,
  canonicalHeaders: string[]
): LegacyTemplateAdaptation => {
  const keys = byNormalizedKey(headerMap)
  // Se parte del mapa original: si el archivo viejo trajera además alguna columna con el
  // nombre vigente (ej "Oficina origen"), sigue disponible para el parser.
  const adapted: Record<string, string> = { ...headerMap }
  const mapped: string[] = []
  const unmapped: string[] = []

  canonicalHeaders.forEach((canonical) => {
    const letter = aliasKeysFor(canonical)
      .map((k) => keys[k])
      .find(Boolean)
    adapted[canonical.toLowerCase()] = letter ?? LEGACY_ABSENT_COLUMN
    if (letter) mapped.push(canonical)
    else unmapped.push(canonical)
  })

  return {
    headerMap: adapted,
    mapped,
    unmapped,
    missingEssential: ESSENTIAL_HEADERS.filter(
      (h) => canonicalHeaders.includes(h) && !mapped.includes(h)
    ),
  }
}
